import { usePrivy } from "@privy-io/react-auth";
import { useExportWallet } from "@privy-io/react-auth/solana";
import { isAppWebView } from "@/utils/webview";

/** Posts a JSON message to the native app via the WebView bridge. */
const postToNative = (payload: { status: "success" | "error" | "cancel"; error?: string }) => {
  const message = JSON.stringify(payload);
  const RNBridge = window.ReactNativeWebView;

  if (isAppWebView() && RNBridge?.postMessage) {
    RNBridge.postMessage(message);
    return;
  }

  console.log("postToNative:", payload);
};

export default function ExportWallet() {
  const { ready, authenticated, login } = usePrivy();
  const { exportWallet } = useExportWallet();
  const isDevelopment = process.env.NODE_ENV !== "production";

  if (!isAppWebView() && !isDevelopment) {
    return (
      <div className="flex flex-col items-center justify-center min-h-dvh p-lg text-center gap-md bg-background text-foreground">
        <h1 className="text-lg font-bold">Unavailable</h1>
        <p className="text-sm text-muted-fg max-w-[300px] leading-relaxed">
          This page can only be accessed from the Goldsand app.
        </p>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="flex flex-col items-center justify-center min-h-dvh p-lg text-center gap-md bg-background text-foreground">
        <p className="text-base text-muted-fg">Loading...</p>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="flex flex-col items-center justify-between min-h-dvh p-lg bg-background text-foreground">
        <div className="flex-1 flex flex-col items-center justify-center text-center gap-md max-w-[340px] mx-auto">
          <h1 className="text-lg font-bold">Wait — are you sure?</h1>
          <p className="text-md text-foreground leading-relaxed">
            You tapped &ldquo;Export wallet.&rdquo; That reveals a secret code that controls your money. If you&apos;re not sure why you&apos;d want that, you almost certainly don&apos;t need to be here.
          </p>
          <p className="text-sm italic text-muted-fg leading-relaxed">
            Goldsand will <span className="font-bold underline">never ask you for this code</span>. Anyone who does is trying to scam you. There is no way to undo someone using it.
          </p>
        </div>
        <div className="flex flex-col items-center gap-md w-full max-w-[340px] pb-md">
          <button
            className="btn-primary h-[52px] w-full rounded-full bg-foreground text-primary-fg text-base font-semibold cursor-pointer shadow-[0_2px_8px_rgba(0,0,0,0.12)]"
            onClick={() => postToNative({ status: "cancel" })}
          >
            Take me back
          </button>
          <button
            className="text-sm text-muted-fg underline cursor-pointer py-sm"
            onClick={() => login({ disableSignup: true, loginMethods: ["email"] })}
          >
            Continue anyway
          </button>
        </div>
      </div>
    );
  }

  const handleExport = async () => {
    try {
      await exportWallet();
      postToNative({ status: "success" });
    } catch (err) {
      postToNative({
        status: "error",
        error: err instanceof Error ? err.message : "Export failed",
      });
    }
  };

  return (
    <div className="flex flex-col items-center justify-between min-h-dvh p-lg bg-background text-foreground">
      <div className="flex-1 flex flex-col items-center justify-center text-center gap-md max-w-[340px] mx-auto">
        <h1 className="text-lg font-bold">One last thing</h1>
        <p className="text-sm text-muted-fg leading-relaxed">
          Your code will appear once. Make sure no one can see your screen.
        </p>
      </div>
      <div className="flex flex-col items-center gap-md w-full max-w-[340px] pb-md">
        <button
          className="btn-primary h-[52px] w-full rounded-full bg-destructive text-white text-base font-semibold cursor-pointer shadow-[0_2px_8px_rgba(239,68,68,0.2)]"
          onClick={handleExport}
        >
          Show my code
        </button>
        <button
          className="text-sm text-muted-fg underline cursor-pointer py-sm"
          onClick={() => postToNative({ status: "cancel" })}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
